import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Box,
  Paper,
  Divider,
  IconButton,
  Alert
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/auth/AuthModule';

const RemoveUserDialog = ({ open, onClose, channels, onRemoveUser }) => {
  const { t } = useTranslation(['common']);
  const { user: currentUser } = useAuth();
  const [selectedChannel, setSelectedChannel] = useState('');
  const [channelMembers, setChannelMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null);

  // Update channel members when selected channel changes
  useEffect(() => {
    if (selectedChannel) {
      const channel = channels.find(c => c._id === selectedChannel);
      if (channel && channel.members) {
        // Filter out the current user (admin) from the list
        const filteredMembers = channel.members.filter(member => 
          member._id !== currentUser._id
        );
        setChannelMembers(filteredMembers);
      } else {
        setChannelMembers([]);
      }
    } else {
      setChannelMembers([]);
    }
  }, [selectedChannel, channels, currentUser]);

  const handleChannelChange = (event) => {
    setSelectedChannel(event.target.value);
  };

  const handleClose = () => {
    setSelectedChannel('');
    setChannelMembers([]);
    setRemovingUserId(null);
    onClose();
  };

  const handleRemoveUser = async (userId) => {
    if (!selectedChannel || !userId) return;
    
    try {
      setLoading(true);
      setRemovingUserId(userId);
      await onRemoveUser(selectedChannel, userId);
      
      // Update the local members list
      setChannelMembers(prev => prev.filter(member => member._id !== userId));
      
      // If no members left, close the dialog
      if (channelMembers.length <= 1) {
        setTimeout(() => {
          handleClose();
        }, 500);
      } else {
        setRemovingUserId(null);
      }
    } catch (error) {
      console.error('Error removing user:', error);
      setRemovingUserId(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('comms.removeUser')}</DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('comms.removeUserDescription')}
        </Typography>
        
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>{t('comms.selectChannel')}</InputLabel>
          <Select
            value={selectedChannel}
            onChange={handleChannelChange}
            disabled={loading}
            label={t('comms.selectChannel')}
          >
            {channels
              .filter(channel => channel.type === 'team') // Only show team channels
              .map(channel => (
                <MenuItem key={channel._id} value={channel._id}>
                  {channel.name}
                </MenuItem>
              ))
            }
          </Select>
        </FormControl>
        
        {selectedChannel && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              {t('comms.channelMembers')}
            </Typography>
            
            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
              {channelMembers.length === 0 ? (
                <Alert severity="info" sx={{ m: 2 }}>
                  {t('comms.noOtherMembersInChannel')}
                </Alert>
              ) : (
                <List dense>
                  {channelMembers.map((member, index) => (
                    <React.Fragment key={member._id}>
                      <ListItem
                        secondaryAction={
                          <IconButton 
                            edge="end" 
                            color="error"
                            onClick={() => handleRemoveUser(member._id)}
                            disabled={loading || removingUserId === member._id}
                          >
                            {removingUserId === member._id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        }
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={member.imageUrl}
                            alt={member.name || member.username}
                          >
                            {(member.name || member.username || '?')[0].toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={member.name || member.username}
                          secondary={member.email}
                        />
                      </ListItem>
                      {index < channelMembers.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveUserDialog; 